import classNames from 'clsx';
import throttle from 'lodash.throttle';
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { initUploadInput } from 'reactApp/appHelpers/appHelpers';
import { isReadOnly } from 'reactApp/modules/home/home.selectors';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getIds, getStorageCurrentFolder } from 'reactApp/modules/storage/storage.selectors';
import { setProvocationShowAction, setUploadFromAction } from 'reactApp/modules/upload/upload.module';
import { RootState } from 'reactApp/store';

import styles from './UploadProvocation.css';
import { sendProvocationGa } from './UploadProvocation.helpers';

const MIN_HEIGHT = 120;
const MAX_HEIGHT = 400;
const FOOTER_HEIGHT = 40;

const UploadProvocationBase = forwardRef(function UploadProvocation(_, ref) {
    const storage = useSelector(getCurrentStorage);
    const [canShow, setShow] = useState<boolean>(false);
    const [height, setHeight] = useState<number>(0);
    const folder = useSelector((state: RootState) => getStorageCurrentFolder(state, storage));
    const isReadOnlyFolder = useSelector((state: RootState) => isReadOnly(state, folder?.id));
    const { isHome } = getStorage(storage);
    const items = useSelector((state: RootState) => getIds(state, storage));
    const viewMode = useSelector((state) => SettingsSelectors.getViewByStorage(state, storage));
    const itemsCount = items.length;
    const isTopBannerShown = Boolean(useSelector(PromoSelectors.getTopBanner));
    const provocationRef = useRef<HTMLDivElement>(null);

    const inputRef = useRef<HTMLInputElement>(null);
    const dispatch = useDispatch();

    useImperativeHandle(ref, () => ({
        canShow: () => canShow,
    }));

    useEffect(() => initUploadInput(inputRef.current), [canShow]);

    const onClick = useCallback(() => {
        dispatch(setUploadFromAction('provocation'));
        sendProvocationGa('click');
    }, []);

    const setProvocationHeight = useCallback(() => {
        if (isReadOnlyFolder || !isHome) {
            return;
        }

        const topBlockPosition = (provocationRef.current?.getBoundingClientRect()?.top || 0) + window.scrollY;

        if (!topBlockPosition) {
            return;
        }

        const blockHeight = window.innerHeight - topBlockPosition - FOOTER_HEIGHT;
        const isFit = blockHeight >= MIN_HEIGHT;
        const provocationHeight = Math.min(blockHeight, MAX_HEIGHT);

        setShow(isFit);
        dispatch(setProvocationShowAction(isFit));

        if (!isFit) {
            return;
        }

        setHeight(provocationHeight);
    }, [isHome, isReadOnlyFolder]);

    const handleOnResize = useCallback(
        // Уменьшать таймаут нельзя, так как на ресайз обрабатывается даталистом тоже не сразу.
        // В итоге, сюда ресайз придет, а даталист еще не переместил элементы и мы получим не то значение topBlockPosition
        throttle(setProvocationHeight, 300),
        [setProvocationHeight]
    );

    useLayoutEffect(() => {
        setTimeout(setProvocationHeight, 300);

        // viewMode, itemsCount в зависимостях для того,
        // чтобы размеры пересчитывались при изменении viewMode и количества элементов
        // isTopBannerShown - промо показывается уже после рендера, поэтому провокация оказывается не того размера
    }, [viewMode, isTopBannerShown, itemsCount, setProvocationHeight]);

    useEffect(() => {
        if (!canShow) {
            return;
        }

        sendProvocationGa('show');
    }, [canShow]);

    useEffect(() => {
        window.addEventListener('resize', handleOnResize);
        return () => {
            window.removeEventListener('resize', handleOnResize);
        };
    }, [handleOnResize]);

    if (isReadOnlyFolder || !isHome) {
        return null;
    }

    return (
        <div
            className={classNames({
                [styles.root]: true,
                [styles.root_hidden]: !canShow,
            })}
            ref={provocationRef}
        >
            {canShow && (
                <>
                    <div className={styles.inner} />
                    <div className={styles.wrapper} onClick={onClick} style={{ height: `${height}px` }}>
                        <input type="file" multiple className={styles.input} ref={inputRef} />
                        <div className={styles.description}>
                            <span className={styles.text}>Нажмите</span> или перенесите
                            <br />
                            файлы для загрузки
                        </div>
                    </div>
                </>
            )}
        </div>
    );
});

export const UploadProvocation = memo(UploadProvocationBase);
